import React from 'react';
import ReactDom from 'react-dom';
import { loadScript } from '@direct-frontend/stdlib';

import { MapInitStatus } from '../constants';
import type { YMapsApi, UseYMapsProps } from '../types';
import { loadAddons } from '../loadAddons';

/**
 * Принимаемые аргументы для метода загрузки скрипта карт
 */
type LoadScriptProps = Pick<UseYMapsProps, 'cspNonce' | 'addons'> & {
    src: string;
};

class SingletonPromise {
    static promise: Promise<
        | {
              maps: YMapsApi['maps'];
              reactify: YMapsApi['reactify'];
              addons: YMapsApi['addons'];
              status: typeof MapInitStatus.READY;
          }
        | {
              status: typeof MapInitStatus.ERROR;
              error: YMapsApi['error'];
          }
    >;

    static createLoadPromise(props: LoadScriptProps) {
        if (!SingletonPromise.promise) {
            SingletonPromise.promise = new Promise(async resolve => {
                try {
                    await loadScript({ crossOrigin: 'disabled', src: props.src, cspNonce: props.cspNonce });
                    await ymaps3.ready;

                    const ymaps3React = await ymaps3.import('@yandex/ymaps3-reactify');
                    const reactifyModule = ymaps3React.reactify.bindTo(React, ReactDom);
                    let addons = null;

                    if (props.addons?.length) {
                        addons = await loadAddons(props.addons, reactifyModule);
                    }

                    resolve({
                        /*
                            TS ожидает тип ReactifiedModule, однако внутри него типовка функций некорректная.
                            Все функции там определены как Context<unknown>, что не позволяет использовать их.
                            Типовка в YMapsApi исправляет этот недочет
                        */
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                        maps: reactifyModule.module(ymaps3) as unknown as YMapsApi['maps'],
                        reactify: reactifyModule,
                        status: MapInitStatus.READY,
                        addons,
                    });
                } catch (error) {
                    resolve({
                        status: MapInitStatus.ERROR,
                        error:
                            error instanceof Error
                                ? error
                                : { name: 'YMaps load script error', message: String(error) },
                    });
                }
            });
        }

        return SingletonPromise.promise;
    }
}

/**
 * Метод загрузка скрипта карт и дополнительных пакетов
 * @kind helper
 * @returns Промис, который резолвится с картами и дополнительными пакетами, либо реджектится с ошибкой
 */
export const loadYMaps = (props: LoadScriptProps) => {
    return SingletonPromise.createLoadPromise(props);
};
